// ChangeLog.js — SCK8 Change Request & QA Log.
// Append-only log at SCK8 CHANGELOG/log (fclm/scan-append, atomic list_append):
//   { op: 'create', uid, fields: {...sections 1-2}, by, at }
//   { op: 'update', uid, fields: {...}, by, at }      later edits (latest wins per field)
//   { op: 'note',   uid, text, by, at }               notes / resolution thread
// Nothing is overwritten or deleted, so every change keeps its author and time.
// REQ numbers follow the order requests reached the log (list_append is atomic).
// Section 3 (Execution & Tracking) is editable by OWNERS; sections 1-2 and the
// verification by the submitter or an owner. Notes: anyone involved.
// NOTE: these edit rules are enforced in this page, not by the API.
import React, { useCallback, useEffect, useMemo, useState } from 'react';

export const OWNERS = ['walsaide'];
export const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent (P1)'];
export const TYPES = ['Accuracy Check', 'Content Update', 'Bug/Broken Link', 'New Page/Section'];
export const STATUSES = ['Backlog', 'In Progress', 'Pending Review', 'Completed', 'On Hold / Blocked'];
export const VERIFY = ['Not verified', 'Verified by requester', 'Needs rework'];
const KEY = { date: 'CHANGELOG', shift: 'log' };
const PRI_COLOR = { Low: '#697576', Medium: '#077398', High: '#FF8F00', 'Urgent (P1)': '#DE1B1B' };
const STATUS_COLOR = { Backlog: '#697576', 'In Progress': '#077398', 'Pending Review': '#8169F8', Completed: '#2E7D32', 'On Hold / Blocked': '#DE1B1B' };
const SUBMITTER_FIELDS = ['requestedBy', 'team', 'priority', 'type', 'targetUrl', 'currentState', 'requestedChange', 'assets'];
const OWNER_FIELDS = ['status', 'assignedTo', 'targetDate', 'dateCompleted'];
const today = () => new Date().toISOString().slice(0, 10);
const fmtTs = s => (s ? new Date(s).toLocaleString([], { month: 'numeric', day: 'numeric', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
// Only http(s) links are clickable (never javascript: etc.).
const safeHref = u => (/^https?:\/\//i.test(String(u || '').trim()) ? String(u).trim() : null);

// Fold the log into requests (pure; unit-tested).
export function foldLog(entries) {
  const byUid = new Map(), order = [];
  for (const e of entries || []) {
    if (!e || !e.uid) continue;
    if (e.op === 'create' && !byUid.has(e.uid)) {
      order.push(e.uid);
      byUid.set(e.uid, { uid: e.uid, num: order.length, ...e.fields, status: 'Backlog', verification: 'Not verified',
        submittedBy: e.by, dateSubmitted: e.at, notes: [], history: [{ by: e.by, at: e.at, what: 'Submitted' }] });
    } else if (e.op === 'update' && byUid.has(e.uid)) {
      const r = byUid.get(e.uid);
      const changed = Object.keys(e.fields || {}).filter(k => r[k] !== e.fields[k]);
      Object.assign(r, e.fields);
      if (changed.length) r.history.push({ by: e.by, at: e.at, what: changed.map(k => `${k} → ${e.fields[k] || '(blank)'}`).join(', ') });
    } else if (e.op === 'note' && byUid.has(e.uid) && e.text) {
      byUid.get(e.uid).notes.push({ by: e.by, at: e.at, text: e.text });
    }
  }
  return order.map(u => ({ ...byUid.get(u), id: `REQ-${String(byUid.get(u).num).padStart(3, '0')}` }));
}

const blank = user => ({ requestedBy: user || '', team: '', priority: 'Medium', type: 'Content Update', targetUrl: '', currentState: '', requestedChange: '', assets: '' });

export default function ChangeLog({ api, site, user }) {
  const me = (user || 'unknown').toLowerCase();
  const isOwner = OWNERS.includes(me);
  const [entries, setEntries] = useState([]);
  const [status, setStatus] = useState('');
  const [draft, setDraft] = useState(null);           // new request form
  const [selUid, setSelUid] = useState(null);
  const [edit, setEdit] = useState({});               // pending field edits for the selected request
  const [note, setNote] = useState('');
  const [filter, setFilter] = useState({ status: 'open', priority: '', type: '', q: '' });

  const load = useCallback(async () => {
    const r = await fetch(`${api}/api/fclm/cache?site=${site}&date=${KEY.date}&shift_type=${KEY.shift}`).then(x => (x.ok ? x.json() : null)).catch(() => null);
    setEntries(r && Array.isArray(r.scans) ? r.scans : []);
  }, [api, site]);
  useEffect(() => { load(); const t = setInterval(load, 60 * 1000); return () => clearInterval(t); }, [load]);

  const append = async rec => {
    const res = await fetch(`${api}/api/fclm/scan-append`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ site, date: KEY.date, shift_type: KEY.shift, scans: [{ ...rec, by: me, at: new Date().toISOString() }] }) });
    setStatus(res.ok ? 'Saved' : 'Save failed — try again');
    if (res.ok) await load();
    return res.ok;
  };

  const requests = useMemo(() => foldLog(entries), [entries]);
  const sel = requests.find(r => r.uid === selUid) || null;
  const canEditSubmitter = sel && (isOwner || sel.submittedBy === me);
  const shown = useMemo(() => {
    const q = filter.q.trim().toLowerCase();
    return requests.filter(r => (filter.status === 'open' ? r.status !== 'Completed' : !filter.status || r.status === filter.status)
      && (!filter.priority || r.priority === filter.priority) && (!filter.type || r.type === filter.type)
      && (!q || [r.id, r.requestedBy, r.team, r.targetUrl, r.currentState, r.requestedChange, r.assignedTo].join(' ').toLowerCase().includes(q)))
      .sort((a, b) => b.num - a.num);
  }, [requests, filter]);

  const submit = async () => {
    if (!draft.requestedBy.trim() || !draft.targetUrl.trim() || !draft.currentState.trim()) { setStatus('Requested by, Target URL and Current state are required'); return; }
    const uid = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const fields = Object.fromEntries(SUBMITTER_FIELDS.map(k => [k, String(draft[k] || '').slice(0, 4000)]));
    if (await append({ op: 'create', uid, fields })) { setDraft(null); setSelUid(uid); }
  };
  const saveEdit = async () => {
    const allowed = [...(canEditSubmitter ? [...SUBMITTER_FIELDS, 'verification'] : []), ...(isOwner ? OWNER_FIELDS : [])];
    const fields = {};
    for (const [k, v] of Object.entries(edit)) if (allowed.includes(k) && v !== sel[k]) fields[k] = String(v ?? '').slice(0, 4000);
    // Completed without a completion date: stamp it.
    if (fields.status === 'Completed' && !(fields.dateCompleted || sel.dateCompleted)) fields.dateCompleted = today();
    if (!Object.keys(fields).length) { setEdit({}); return; }
    if (await append({ op: 'update', uid: sel.uid, fields })) setEdit({});
  };
  const addNote = async () => { if (note.trim() && (await append({ op: 'note', uid: sel.uid, text: note.trim().slice(0, 4000) }))) setNote(''); };

  const card = { background: '#fff', border: '1px solid #E7E9E9', borderRadius: 8, padding: 16, marginBottom: 16 };
  const th = { textAlign: 'left', fontSize: 12, color: '#697576', padding: '6px 8px', borderBottom: '1px solid #E7E9E9', whiteSpace: 'nowrap' };
  const td = { fontSize: 13, padding: '6px 8px', borderBottom: '1px solid #E7E9E9', verticalAlign: 'top' };
  const sec = { fontSize: 13, fontWeight: 700, margin: '14px 0 6px', color: '#232F3E' };
  const lab = { fontSize: 12, display: 'block', marginBottom: 8 };
  const inp = { width: '100%', boxSizing: 'border-box' };
  const val = (k) => (k in edit ? edit[k] : sel?.[k] ?? '');
  const field = (k, label, opts, { multi, type, editable } = {}) => (
    <label style={lab} key={k}>{label}<br />
      {!editable ? <span style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{k === 'targetUrl' && safeHref(sel[k]) ? <a href={safeHref(sel[k])} target="_blank" rel="noopener noreferrer">{sel[k]}</a> : (sel[k] || '—')}</span>
        : opts ? <select value={val(k)} onChange={e => setEdit({ ...edit, [k]: e.target.value })} style={inp} aria-label={label}>{opts.map(o => <option key={o}>{o}</option>)}</select>
        : multi ? <textarea rows={3} value={val(k)} onChange={e => setEdit({ ...edit, [k]: e.target.value })} style={inp} aria-label={label} />
        : <input type={type || 'text'} value={val(k)} onChange={e => setEdit({ ...edit, [k]: e.target.value })} style={inp} aria-label={label} />}
    </label>
  );
  const dfield = (k, label, opts, { multi, required } = {}) => (
    <label style={lab} key={k}>{label}{required ? ' *' : ''}<br />
      {opts ? <select value={draft[k]} onChange={e => setDraft({ ...draft, [k]: e.target.value })} style={inp} aria-label={label}>{opts.map(o => <option key={o}>{o}</option>)}</select>
        : multi ? <textarea rows={3} value={draft[k]} onChange={e => setDraft({ ...draft, [k]: e.target.value })} style={inp} aria-label={label} />
        : <input value={draft[k]} onChange={e => setDraft({ ...draft, [k]: e.target.value })} style={inp} aria-label={label} />}
    </label>
  );
  const pill = (t, c) => <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: c || '#697576', borderRadius: 10, padding: '2px 8px', whiteSpace: 'nowrap' }}>{t}</span>;

  return (
    <div>
      <h2 className="section-title">Change Request & QA Log — {site}</h2>
      <div style={{ ...card, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={() => { setDraft(blank(user)); setSelUid(null); }}>+ New request</button>
        <label style={{ fontSize: 12 }}>Status<br /><select value={filter.status} onChange={e => setFilter({ ...filter, status: e.target.value })} aria-label="Filter status"><option value="open">Open (not completed)</option><option value="">All</option>{STATUSES.map(s => <option key={s}>{s}</option>)}</select></label>
        <label style={{ fontSize: 12 }}>Priority<br /><select value={filter.priority} onChange={e => setFilter({ ...filter, priority: e.target.value })} aria-label="Filter priority"><option value="">All</option>{PRIORITIES.map(s => <option key={s}>{s}</option>)}</select></label>
        <label style={{ fontSize: 12 }}>Type<br /><select value={filter.type} onChange={e => setFilter({ ...filter, type: e.target.value })} aria-label="Filter type"><option value="">All</option>{TYPES.map(s => <option key={s}>{s}</option>)}</select></label>
        <label style={{ fontSize: 12, flex: 1, minWidth: 180 }}>Search<br /><input value={filter.q} onChange={e => setFilter({ ...filter, q: e.target.value })} style={inp} placeholder="ID, name, URL, text…" aria-label="Search requests" /></label>
        <span style={{ fontSize: 12, color: '#697576' }}>{requests.length} request(s) · {requests.filter(r => r.status !== 'Completed').length} open {status && `· ${status}`}</span>
      </div>

      {draft && (
        <div style={{ ...card, borderColor: '#077398' }}>
          <strong style={{ fontSize: 15 }}>New change request</strong>
          <div style={sec}>1. Request details</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            <label style={lab}>ID<br /><span style={{ fontSize: 13 }}>REQ-{String(requests.length + 1).padStart(3, '0')} (assigned on submit)</span></label>
            <label style={lab}>Date submitted<br /><span style={{ fontSize: 13 }}>{today()} (stamped on submit)</span></label>
            {dfield('requestedBy', 'Requested by (name)', null, { required: true })}
            {dfield('team', 'Team / department')}
            {dfield('priority', 'Priority', PRIORITIES)}
            {dfield('type', 'Type', TYPES)}
          </div>
          {dfield('targetUrl', 'Target URL / path', null, { required: true })}
          <div style={sec}>2. Scope & description</div>
          {dfield('currentState', 'Current state / issue — what is incorrect, outdated or missing?', null, { multi: true, required: true })}
          {dfield('requestedChange', 'Requested change / exact text — new copy, imagery guidelines or corrected values', null, { multi: true })}
          {dfield('assets', 'Assets / reference links — links to data sources', null, { multi: true })}
          <div style={{ display: 'flex', gap: 8 }}><button className="btn btn-primary" onClick={submit}>Submit request</button><button className="btn" onClick={() => setDraft(null)}>Cancel</button></div>
        </div>
      )}

      {sel && !draft && (
        <div style={{ ...card, borderColor: STATUS_COLOR[sel.status] || '#077398' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <strong style={{ fontSize: 15 }}>{sel.id} {pill(sel.priority, PRI_COLOR[sel.priority])} {pill(sel.status, STATUS_COLOR[sel.status])}</strong>
            <button className="btn" onClick={() => { setSelUid(null); setEdit({}); }}>Close</button>
          </div>
          <div style={sec}>1. Request details</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            <label style={lab}>Date submitted<br /><span style={{ fontSize: 13 }}>{fmtTs(sel.dateSubmitted)} by {sel.submittedBy}</span></label>
            {field('requestedBy', 'Requested by', null, { editable: canEditSubmitter })}
            {field('team', 'Team / department', null, { editable: canEditSubmitter })}
            {field('priority', 'Priority', PRIORITIES, { editable: canEditSubmitter })}
            {field('type', 'Type', TYPES, { editable: canEditSubmitter })}
          </div>
          {field('targetUrl', 'Target URL / path', null, { editable: canEditSubmitter })}
          <div style={sec}>2. Scope & description</div>
          {field('currentState', 'Current state / issue', null, { multi: true, editable: canEditSubmitter })}
          {field('requestedChange', 'Requested change / exact text', null, { multi: true, editable: canEditSubmitter })}
          {field('assets', 'Assets / reference links', null, { multi: true, editable: canEditSubmitter })}
          <div style={sec}>3. Execution & tracking {!isOwner && <span style={{ fontWeight: 400, color: '#697576' }}>(updated by {OWNERS.join(', ')})</span>}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            {field('status', 'Status', STATUSES, { editable: isOwner })}
            {field('assignedTo', 'Assigned to', null, { editable: isOwner })}
            {field('targetDate', 'Target completion date', null, { type: 'date', editable: isOwner })}
            {field('dateCompleted', 'Date completed', null, { type: 'date', editable: isOwner })}
          </div>
          <div style={sec}>4. Verification & notes</div>
          {field('verification', 'Completion verification', VERIFY, { editable: canEditSubmitter })}
          {(isOwner || canEditSubmitter) && <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}><button className="btn btn-primary" onClick={saveEdit} disabled={!Object.keys(edit).length}>Save changes</button>{Object.keys(edit).length > 0 && <button className="btn" onClick={() => setEdit({})}>Discard</button>}</div>}
          <div style={{ fontSize: 12, fontWeight: 700, marginTop: 6 }}>Notes / resolution</div>
          {sel.notes.length === 0 && <div style={{ fontSize: 12, color: '#697576' }}>No notes yet.</div>}
          {sel.notes.map((n, i) => <div key={i} style={{ fontSize: 13, borderLeft: '3px solid #E7E9E9', padding: '4px 8px', margin: '6px 0', whiteSpace: 'pre-wrap' }}><span style={{ fontSize: 11, color: '#697576' }}>{n.by} · {fmtTs(n.at)}</span><br />{n.text}</div>)}
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <textarea rows={2} value={note} onChange={e => setNote(e.target.value)} style={{ flex: 1 }} placeholder="Context on the change, or reason for a delay / blocker" aria-label="Add a note" />
            <button className="btn" onClick={addNote} disabled={!note.trim()}>Add note</button>
          </div>
          <details style={{ marginTop: 10 }}><summary style={{ fontSize: 12, cursor: 'pointer' }}>Change history ({sel.history.length})</summary>
            {sel.history.map((h, i) => <div key={i} style={{ fontSize: 12, color: '#697576' }}>{fmtTs(h.at)} · {h.by} · {h.what}</div>)}
          </details>
        </div>
      )}

      <div style={card}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead><tr>{['ID', 'Submitted', 'Requested by', 'Priority', 'Type', 'Target', 'Status', 'Assigned', 'Target date', 'Completed', 'Verified'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {shown.length === 0 && <tr><td style={{ ...td, color: '#697576' }} colSpan={11}>No requests match. Use “+ New request” to add one.</td></tr>}
            {shown.map(r => (
              <tr key={r.uid} onClick={() => { setSelUid(r.uid); setDraft(null); setEdit({}); }} style={{ cursor: 'pointer', background: r.uid === selUid ? '#EAF6FB' : undefined }}>
                <td style={{ ...td, fontWeight: 700 }}>{r.id}</td><td style={td}>{fmtTs(r.dateSubmitted)}</td>
                <td style={td}>{r.requestedBy}{r.team ? <span style={{ color: '#697576' }}> · {r.team}</span> : null}</td>
                <td style={td}>{pill(r.priority, PRI_COLOR[r.priority])}</td><td style={td}>{r.type}</td>
                <td style={{ ...td, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.targetUrl}>{r.targetUrl}</td>
                <td style={td}>{pill(r.status, STATUS_COLOR[r.status])}</td><td style={td}>{r.assignedTo || '—'}</td>
                <td style={td}>{r.targetDate || '—'}</td><td style={td}>{r.dateCompleted || '—'}</td><td style={td}>{r.verification}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
